import type { MapStyleId } from './controlCenterMapTypes'

export const MAP_STYLE_STORAGE_KEY = 'controlCenter.mapStyleId'
export const OUTPUT_PANEL_STORAGE_KEY = 'controlCenter.outputPanelOpen'

const DAISY_DARK_THEMES = new Set<string>([
  'dark',
  'synthwave',
  'halloween',
  'forest',
  'aqua',
  'black',
  'luxury',
  'dracula',
  'business',
  'night',
  'coffee',
  'dim',
  'sunset',
  'abyss',
  'silk',
])

export const MAP_STYLES: Record<
  MapStyleId,
  { label: string; url: string; attribution: string; maxZoom?: number }
> = {
  streets: {
    label: 'Streets',
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    maxZoom: 19,
  },
  terrain: {
    label: 'Terrain',
    url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',
    attribution:
      '&copy; <a href="https://opentopomap.org">OpenTopoMap</a> (<a href="https://creativecommons.org/licenses/by-sa/3.0/">CC-BY-SA</a>), &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    maxZoom: 17,
  },
  dark: {
    label: 'Dark',
    url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
    attribution:
      '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>, &copy; <a href="https://carto.com/attributions">CARTO</a>',
    maxZoom: 20,
  },
  satellite: {
    label: 'Satellite',
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution:
      'Tiles &copy; <a href="https://www.esri.com/">Esri</a> — Source: Esri, Maxar, Earthstar Geographics, and the GIS User Community',
    maxZoom: 19,
  },
}

export function isMapStyleId(value: unknown): value is MapStyleId {
  return value === 'streets' || value === 'terrain' || value === 'dark' || value === 'satellite'
}

export function getCurrentDaisyTheme(): string {
  if (typeof document !== 'undefined') {
    const themeAttr = document.documentElement.getAttribute('data-theme')
    if (themeAttr) return String(themeAttr).trim().toLowerCase()
  }
  if (typeof localStorage !== 'undefined') {
    const savedTheme = localStorage.getItem('theme')
    if (savedTheme) return String(savedTheme).trim().toLowerCase()
  }
  return 'light'
}

export function getThemeDefaultMapStyle(): MapStyleId {
  return DAISY_DARK_THEMES.has(getCurrentDaisyTheme()) ? 'dark' : 'streets'
}

export function getInitialMapStyle(): MapStyleId {
  if (typeof localStorage !== 'undefined') {
    const savedMapStyle = localStorage.getItem(MAP_STYLE_STORAGE_KEY)
    if (isMapStyleId(savedMapStyle)) return savedMapStyle
  }
  return getThemeDefaultMapStyle()
}

export function getInitialPanelOpen(storageKey: string, defaultOpen: boolean): boolean {
  if (typeof localStorage === 'undefined') return defaultOpen
  const stored = localStorage.getItem(storageKey)
  if (stored === null) return defaultOpen
  return stored === '1'
}

export function getInitialOutputOpen(): boolean {
  return getInitialPanelOpen(OUTPUT_PANEL_STORAGE_KEY, true)
}

